import { Fragment, useEffect } from 'react';
import { View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { Divider, Spinner, useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashFilter from 'lodash/filter';
import lodashGet from 'lodash/get';

import ReservationIcon from '../../Assets/reservation.svg';
import EmptyData from '../../Components/EmptyData';
import ListItem from '../../Components/ListItem';
import { textCategory } from '../../Components/Reservation/constants';
import ResponsiveText from '../../Components/ResponsiveText';
import { ReservationType } from '../../Components/Sheets/BookReservation/types';
import TicketIcon from '../../Components/SVG/Tickets';
import constants from '../../Config/constants';
import messages from '../../Config/messages';
import useReservationBooking from '../../Hooks/useReservationBooking';
import routeList, {
  ReservationNavigatorProps,
  RootRouteProps,
} from '../../Routes/list';
import { useGetEventsQuery } from '../../RTK/api/events';
import { EventDetails } from '../../RTK/api/events/types';
import { useGetTablesQuery } from '../../RTK/api/reservations';
import { TableDetails } from '../../RTK/api/reservations/types';
import { useGetStoreDetailsQuery } from '../../RTK/api/store';
import { changeReservationStoreFilter } from '../../RTK/filter';
import { selectReservationStoreFilter } from '../../RTK/filter/selectors';
import themeStyle from '../../Theme/styles';

import EventItem from './components/EventItem';
import ReservationItem from './components/ReservationItem';

type Props = {
  storeId: string;
};

const TABS = [
  {
    label: 'Available Reservations',
    value: constants.RESERVATION_TYPE.DINING,
    icon: {
      leftIcon: { svg: ReservationIcon, size: 14 },
    },
  },
  {
    label: 'Available Events',
    value: constants.RESERVATION_TYPE.EVENT,
    icon: { leftIcon: { svg: TicketIcon, size: 24 } },
  },
];

const Reservations = ({ storeId }: Props) => {
  const dispatch = useDispatch();
  const navigation =
    useNavigation<NavigationProp<RootRouteProps & ReservationNavigatorProps>>();
  const route = useRoute();
  const { showBookingSchedulePicker, navigateToCheckoutDestination } =
    useReservationBooking();
  const theme = useTheme();
  const filter = useSelector(selectReservationStoreFilter);

  const defaultTab =
    lodashGet(route, 'params.defaultTab') || constants.RESERVATION_TYPE.DINING;
  const isExclusiveBooking =
    lodashGet(route, 'params.exclusiveBooking') === '1';
  const isEvent = filter.tab === constants.RESERVATION_TYPE.EVENT;
  const isDining = filter.tab === constants.RESERVATION_TYPE.DINING;

  const { data: storeInfo } = useGetStoreDetailsQuery(
    { payload: storeId },
    { skip: !storeId },
  );

  const skipEventRequest = !storeId || !filter.eventDate || !filter.partySize;
  const { data: storeEvents, isFetching } = useGetEventsQuery(
    {
      data: {
        isNextMonth: dayjs(filter.eventDate).month() === dayjs().month() + 1,
      },
      payload: {
        store_id: storeId,
        date: filter.eventDate,
        limit: 100,
        offset: 0,
        party_size: filter.partySize,
        store: storeId,
      },
    },
    {
      skip: skipEventRequest,
      refetchOnMountOrArgChange: true, // so when reopen the store page on reservation it refresh the list
    },
  );

  const skipTableRequest =
    !storeId || !filter.tableDate || !filter.tableTime || !filter.partySize;
  const { data: storeTables, isFetching: tablesIsFetching } = useGetTablesQuery(
    {
      payload: {
        store_id: storeId,
        date: filter.tableDate,
        time: filter.tableTime,
        party_size: filter.partySize,
      },
    },
    {
      skip: skipTableRequest,
      refetchOnMountOrArgChange: true, // so when reopen the store page on reservation it refresh the list
    },
  );

  useEffect(() => {
    dispatch(changeReservationStoreFilter({ tab: defaultTab }));
  }, [defaultTab, dispatch]);

  const _onGetTickets = async (data: EventDetails) => {
    navigation.navigate(routeList.RES_EVENT_DETAILS, {
      eventId: data.id,
      size: filter.partySize,
    });
  };

  const _onBookNow = async (data: TableDetails) => {
    const result = await showBookingSchedulePicker({
      filter: {
        size: filter.partySize,
        date: filter.tableDate,
        time: filter.tableTime,
      },
      itemInfo: { ...data, type: ReservationType.DINING },
      storeInfo,
    });
    if (result?.success) {
      navigateToCheckoutDestination(navigation, result.data);
    }
  };

  const _onTabChanged = (tab) => {
    // no need to dispatch here, only update url parameter
    // since this component effect handle the sync of url parameter to redux state
    navigation.setParams({ defaultTab: tab.value });
  };

  if (isFetching || tablesIsFetching) {
    return (
      <View
        style={[
          themeStyle.pageVerticalSpacing,
          themeStyle.flexDirectionRowCenterCenter,
          themeStyle.spacingBottomMedium,
        ]}
      >
        <Spinner status="info" size="large" />
      </View>
    );
  }

  return (
    <View>
      <View style={themeStyle.flexDirectionRow}>
        {(isExclusiveBooking
          ? lodashFilter(TABS, { value: constants.RESERVATION_TYPE.DINING })
          : TABS
        ).map((rt, i) => {
          const isActive = filter.tab === rt.value;
          return (
            <View
              key={i}
              style={[
                themeStyle.spacingRight,
                isActive && {
                  borderBottomWidth: 1,
                  borderBottomColor: theme['color-info-500'],
                },
              ]}
            >
              <ListItem
                contentContainerStyle={[
                  themeStyle.pageVerticalSpacingSmall,
                  themeStyle.spacingRightSmall,
                ]}
                flex={false}
                title={
                  <ResponsiveText
                    category={textCategory.HEADING}
                    status={isActive ? 'info' : 'basic'}
                  >
                    {rt.label}
                  </ResponsiveText>
                }
                onPress={() => _onTabChanged(rt)}
                {...rt.icon}
                leftIcon={{
                  ...rt.icon.leftIcon,
                  color: isActive
                    ? theme['color-info-500']
                    : theme['color-primary-500'],
                }}
              />
            </View>
          );
        })}
      </View>
      {/* Event List for this store */}
      {isDining &&
        (storeTables?.length > 0 ? (
          storeTables.map((item, i) => (
            <Fragment key={item.id}>
              <View style={themeStyle.pageVerticalSpacing}>
                <ReservationItem
                  date={filter.tableDate}
                  size={filter.partySize}
                  time={filter.tableTime}
                  store={storeInfo}
                  onBookNow={() => _onBookNow(item)}
                  {...item}
                />
              </View>
              {i !== storeTables.length - 1 && (
                <Divider style={[themeStyle.divider]} />
              )}
            </Fragment>
          ))
        ) : (
          <View style={[themeStyle.flex1, themeStyle.pageVerticalSpacing]}>
            <EmptyData
              fill={theme['color-info-500']}
              title={(() => {
                if (skipTableRequest) {
                  return messages.RESERVATION_SEARCH.title;
                }
                if (filter.partySize >= constants.LARGE_PARTY_SIZE_THRESHOLD) {
                  return 'Bringing the whole crew?';
                }
                return 'No reservation found.';
              })()}
              subtitle={(() => {
                if (skipTableRequest) {
                  return messages.RESERVATION_SEARCH.message;
                }
                if (filter.partySize >= constants.LARGE_PARTY_SIZE_THRESHOLD) {
                  return `For parties of ${constants.LARGE_PARTY_SIZE_THRESHOLD} or above, please message us directly`;
                }
                return '';
              })()}
              type="group"
              svgWidth={400}
            />
          </View>
        ))}

      {isEvent &&
        (storeEvents?.result?.length > 0 ? (
          storeEvents.result.map((item, i) => (
            <Fragment key={item.id}>
              <View style={themeStyle.pageVerticalSpacing}>
                <EventItem {...item} onBookNow={_onGetTickets} />
              </View>
              {i !== storeEvents.result.length - 1 && (
                <Divider style={[themeStyle.divider]} />
              )}
            </Fragment>
          ))
        ) : (
          <View
            style={[
              themeStyle.pageVerticalSpacing,
              themeStyle.pageHorizontalSpacing,
            ]}
          >
            <EmptyData
              fill={theme['color-info-500']}
              title={
                skipTableRequest
                  ? messages.RESERVATION_SEARCH.title
                  : 'No events found.'
              }
              subtitle={
                skipTableRequest ? messages.RESERVATION_SEARCH.message : ''
              }
              type="filter"
            />
          </View>
        ))}
    </View>
  );
};

export default Reservations;
